import type {
  AllegroFullImpliedWarranty,
  AllegroImpliedWarranty,
} from '../../../api/allegro/impliedwarranty/impliedWarrantyTypes';
import type { ExcelRow } from '../../excel/types';
import { getAllegroImpliedWarranty } from '../../../api/allegro/impliedwarranty/impliedWarranty';
import { serializeExcel } from '../../excel/serializeExcel';
import { XLSX_MIME_TYPE } from '../../excel/constants';
import { downloadData } from '../../../utils/downloadData';
import { swallowUnauthorized } from '../utils';
import COLUMNS from './impliedWarrantyExcelSchema';

const SHEET_NAME = 'Implied warranties';
const DEFAULT_FILE_NAME = 'implied-warranties.xlsx';

function fetchFullWarranties(
  marketplaceCode: string,
  warranties: AllegroImpliedWarranty[],
): Promise<AllegroFullImpliedWarranty[]> {
  return Promise.all(
    warranties.map((warranty) => getAllegroImpliedWarranty(marketplaceCode, warranty.id)),
  );
}

function mapExcelRow(warranty: AllegroFullImpliedWarranty): ExcelRow {
  const { address } = warranty;

  return {
    [COLUMNS.id]: warranty.id,
    [COLUMNS.label]: warranty.name,
    [COLUMNS.individualPeriod]: warranty.individual?.period ?? undefined,
    [COLUMNS.corporatePeriod]: warranty.corporate?.period ?? undefined,
    [COLUMNS.name]: address?.name ?? undefined,
    [COLUMNS.street]: address?.street ?? undefined,
    [COLUMNS.postCode]: address?.postCode ?? undefined,
    [COLUMNS.city]: address?.city ?? undefined,
    [COLUMNS.countryCode]: address?.countryCode ?? undefined,
    [COLUMNS.description]: warranty.description ?? undefined,
  };
}

export default async function downloadImpliedWarrantiesExcel(
  marketplaceCode: string,
  warranties: AllegroImpliedWarranty[],
  fileName: string = DEFAULT_FILE_NAME,
): Promise<void> {
  const fullWarranties = await swallowUnauthorized(
    () => fetchFullWarranties(marketplaceCode, warranties),
    undefined,
  );

  if (!fullWarranties) {
    return;
  }

  const rows = fullWarranties.map(mapExcelRow);
  const blob = await serializeExcel(rows, {
    sheetName: SHEET_NAME,
    headers: Object.values(COLUMNS),
  });

  downloadData(blob, XLSX_MIME_TYPE, fileName);
}
